import {
  listConversations as apiListConversations,
  getConversationState,
  createConversation as apiCreateConversation,
  archiveConversation as apiArchiveConversation,
  deleteConversation as apiDeleteConversation,
  patchConversation as apiPatchConversation,
  duplicateConversation as apiDuplicateConversation,
} from "./blynApiClient";
import { getTenantId as resolveTenantId, getUserId as resolveUserId } from "./currentUser";
import { toastApiError } from "./apiToast";

function formatRel(iso: string | null | undefined): string {
  if (!iso) return "—";
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return "—";
  const now = new Date();
  if (y === now.getFullYear() && m === now.getMonth() + 1 && d === now.getDate()) return "today";
  const date = new Date(y, m - 1, d);
  const sameYear = y === now.getFullYear();
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(!sameYear && { year: "numeric" }) });
}

export interface BlynConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export interface BlynConversation {
  id: string;
  title: string;
  projectId: string | null;
  updatedAt: string;
  turns: number;
  artifacts?: number;
  preview: string;
  messages?: BlynConversationMessage[];
}

function getTenantId() {
  return resolveTenantId();
}

function getUserId() {
  return resolveUserId();
}

export async function listConversations(): Promise<BlynConversation[]> {
  try {
    const res = await apiListConversations(getTenantId(), undefined, 100);
    return res.items.map(c => ({
      id: c.conversation_id,
      title: c.title,
      projectId: c.project_id,
      updatedAt: formatRel(c.updated_at),
      turns: c.turns,
      artifacts: 0,
      preview: c.preview
    }));
  } catch (err) {
    toastApiError("Failed to load conversations", err);
    return [];
  }
}

export async function listConversationsForProject(
  projectId: string
): Promise<BlynConversation[]> {
  try {
    const res = await apiListConversations(getTenantId(), projectId);
    return res.items.map(c => ({
      id: c.conversation_id,
      title: c.title,
      projectId: c.project_id,
      updatedAt: formatRel(c.updated_at),
      turns: c.turns,
      artifacts: 0,
      preview: c.preview
    }));
  } catch (err) {
    toastApiError("Failed to load project conversations", err);
    return [];
  }
}

export async function createNewConversation(title?: string, projectId?: string | null): Promise<string> {
  try {
    const res = await apiCreateConversation({
      tenant_id: getTenantId(),
      user_id: getUserId(),
      title,
      project_id: projectId,
    });
    return res.conversation_id;
  } catch (err) {
    toastApiError("Failed to create conversation", err);
    return crypto.randomUUID();
  }
}

export async function archiveConversation(conversationId: string): Promise<boolean> {
  try {
    await apiArchiveConversation(conversationId, getTenantId());
    return true;
  } catch (err) {
    toastApiError("Failed to archive conversation", err);
    return false;
  }
}

export async function deleteConversation(conversationId: string): Promise<boolean> {
  try {
    await apiDeleteConversation(conversationId, getTenantId());
    return true;
  } catch (err) {
    toastApiError("Failed to delete conversation", err);
    return false;
  }
}

export async function duplicateConversation(conversationId: string): Promise<BlynConversation | null> {
  try {
    const state = await apiDuplicateConversation(conversationId, getTenantId());
    return {
      id: state.conversation_id,
      title: state.title || "Untitled",
      projectId: state.project_id || null,
      updatedAt: formatRel(state.updated_at),
      turns: state.turns || 0,
      preview: state.preview || "",
    };
  } catch (err) {
    toastApiError("Failed to duplicate conversation", err);
    return null;
  }
}

export async function renameConversation(conversationId: string, title: string): Promise<boolean> {
  try {
    await apiPatchConversation(conversationId, { tenant_id: getTenantId(), title });
    return true;
  } catch (err) {
    toastApiError("Failed to rename conversation", err);
    return false;
  }
}

export async function moveConversationToProject(
  conversationId: string,
  projectId: string | null
): Promise<boolean> {
  try {
    await apiPatchConversation(conversationId, { tenant_id: getTenantId(), project_id: projectId });
    return true;
  } catch (err) {
    toastApiError("Failed to move conversation", err);
    return false;
  }
}

export async function getConversation(
  conversationId: string
): Promise<BlynConversation | null> {
  try {
    const state = await getConversationState(conversationId, getTenantId());
    return {
      id: state.conversation_id || conversationId,
      title: state.title || "Untitled",
      projectId: state.project_id || null,
      updatedAt: formatRel(state.updated_at),
      turns: state.turns || 0,
      preview: state.preview || "",
      messages: Array.isArray(state.messages)
        ? state.messages
            .filter((m: any) => m.role === 'user' || m.role === 'assistant')
            .map((m: any) => ({ id: m.id, role: m.role as 'user' | 'assistant', content: m.content }))
        : undefined,
    };
  } catch (err) {
    console.error("[getConversation] Error loading conversation:", err);
    toastApiError("Failed to load conversation", err);
    return null;
  }
}
